import { Injectable, inject } from '@angular/core';
import {
  type ActivatedRouteSnapshot,
  type RouterStateSnapshot,
  type UrlTree,
  Router,
} from '@angular/router';
import { FeatureFlagsService } from '@principle-theorem/ng-feature-flags';
import { firstValueFrom } from '@principle-theorem/shared';
import { AI_SUITE_FEATURES } from './ai-suite-feature-registry';

/**
 * Registry-driven default-child guard for the AI Suite shell.
 *
 * Iterates `AI_SUITE_FEATURES` and redirects to the first feature
 * whose `IFeature` is enabled for the current workspace. If none
 * are enabled, redirects to the brand root.
 */
@Injectable({
  providedIn: 'root',
})
export class AiSuiteDefaultChildResolver {
  private readonly _featureFlags = inject(FeatureFlagsService);
  private readonly _router = inject(Router);

  async canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<UrlTree> {
    for (const feature of AI_SUITE_FEATURES) {
      const enabled = await firstValueFrom(
        this._featureFlags.isFeatureEnabled$(feature.feature)
      );
      if (enabled) {
        const parentUrl = state.url.replace(/\/$/, '');
        return this._router.parseUrl(`${parentUrl}/${feature.id}`);
      }
    }

    // No features enabled — redirect to brand root
    const segments = state.url.split('/').filter(Boolean);
    return this._router.parseUrl(`/${segments[0] ?? ''}`);
  }
}
